import { useState } from 'react';
import { useConvex, useMutation, useQuery } from 'convex/react';
import { api } from '../../../../convex/_generated/api';
import type { Id } from '../../../../convex/_generated/dataModel';

type DateMode = 'day-first' | 'month-first';

/**
 * Migration.
 *
 * Always a dry run first. The operator sees exactly what will land, what will be skipped and
 * why, and — critically — how the file's dates were interpreted, before anything is written.
 */
export default function Import() {
  const convex = useConvex();
  const profiles = useQuery(api.imports.profiles, {});
  const clients = useQuery(api.clients.list, {});
  const batches = useQuery(api.imports.batches, {});
  const apply = useMutation(api.imports.apply);

  const [csv, setCsv] = useState('');
  const [fileName, setFileName] = useState('pasted.csv');
  const [profile, setProfile] = useState('winpats');
  const [clientId, setClientId] = useState<string>('');
  const [dateMode, setDateMode] = useState<DateMode | ''>('');
  const [preview, setPreview] = useState<Awaited<ReturnType<typeof runDryRun>> | null>(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState<string | null>(null);

  async function runDryRun() {
    return convex.query(api.imports.dryRun, {
      csv,
      profile,
      ...(dateMode ? { dateMode } : {}),
    });
  }

  async function onFile(file: File) {
    setFileName(file.name);
    setCsv(await file.text());
    setPreview(null);
  }

  return (
    <>
      <h1>Import history</h1>
      <p className="muted">
        Bring an operator's existing records across from WinPATS, PATGuard, Metrel or a
        spreadsheet. Nothing is written until you have seen the preview.
      </p>

      <div className="card">
        <label>Export file</label>
        <input
          type="file"
          accept=".csv,text/csv,text/plain"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) void onFile(file);
          }}
        />
        <label>…or paste the file contents</label>
        <textarea
          value={csv}
          onChange={(e) => { setCsv(e.target.value); setPreview(null); }}
          placeholder="Asset No,Description,Test Date,Test Result…"
        />

        <label>Source</label>
        <select value={profile} onChange={(e) => { setProfile(e.target.value); setPreview(null); }}>
          {(profiles ?? []).map((p) => (
            <option key={p.key} value={p.key}>{p.label}</option>
          ))}
        </select>

        <label>Import into</label>
        <select value={clientId} onChange={(e) => setClientId(e.target.value)}>
          <option value="">Choose a client…</option>
          {(clients ?? []).map((c) => (
            <option key={c._id} value={c._id}>{c.name}</option>
          ))}
        </select>

        <button
          style={{ marginTop: '.75rem' }}
          disabled={!csv.trim() || busy}
          onClick={async () => {
            setBusy(true);
            try { setPreview(await runDryRun()); } finally { setBusy(false); }
          }}
        >
          {busy ? 'Reading…' : 'Preview import'}
        </button>
      </div>

      {preview ? (
        <>
          {!preview.dateConvention.proven ? (
            <div className="card" style={{ borderColor: 'var(--alert)' }}>
              <strong style={{ color: 'var(--alert)' }}>Check the dates before importing.</strong>
              <p className="small">{preview.dateConvention.evidence}</p>
              <p className="small muted">
                Reading them the wrong way round would move every test and every retest date in
                this file.
              </p>
              <div className="row">
                <button
                  className={dateMode !== 'month-first' ? '' : 'quiet'}
                  onClick={async () => { setDateMode('day-first'); setPreview(await runDryRun()); }}
                >
                  Day first (25/04 = 25 April)
                </button>
                <button
                  className={dateMode === 'month-first' ? '' : 'quiet'}
                  onClick={async () => { setDateMode('month-first'); setPreview(await runDryRun()); }}
                >
                  Month first (04/25 = 25 April)
                </button>
              </div>
            </div>
          ) : (
            <div className="card">
              <span className="pill">dates: {preview.dateMode}</span>{' '}
              <span className="muted small">{preview.dateConvention.evidence}</span>
            </div>
          )}

          <div className="tiles">
            <div className="tile"><span className="n">{preview.summary.distinctTags}</span><span className="l">items</span></div>
            <div className="tile"><span className="n">{preview.summary.newAssets}</span><span className="l">new</span></div>
            <div className="tile"><span className="n">{preview.summary.existingAssets}</span><span className="l">already known</span></div>
            <div className="tile"><span className="n">{preview.summary.withTest}</span><span className="l">tests to import</span></div>
            <div className={`tile${preview.summary.problems ? ' alert' : ''}`}>
              <span className="n">{preview.summary.problems}</span><span className="l">rows with problems</span>
            </div>
          </div>

          {preview.unmappedHeaders.length > 0 ? (
            <div className="card">
              <strong>Columns we did not recognise</strong>
              <p className="small muted">
                These are ignored. If one of them holds your tag numbers or test dates, pick a
                different source or tell us and we will add it.
              </p>
              <p className="small">{preview.unmappedHeaders.join(' · ')}</p>
            </div>
          ) : null}

          <div className="card">
            <strong>First {Math.min(preview.rows.length, 25)} rows</strong>
            <table>
              <thead><tr><th>#</th><th>Tag</th><th>Item</th><th>Tested</th><th>Result</th><th>Problems</th></tr></thead>
              <tbody>
                {preview.rows.slice(0, 25).map((r) => (
                  <tr key={r.rowNumber}>
                    <td className="muted small">{r.rowNumber}</td>
                    <td className="tag">{r.tagId || '—'}</td>
                    <td>{r.description || '—'}</td>
                    <td>{r.testedAt ? new Date(r.testedAt).toLocaleDateString('en-AU') : '—'}</td>
                    <td>{r.result ?? '—'}</td>
                    <td className={r.problems.length ? 'overdue' : 'muted'}>
                      {r.problems.join('; ') || 'ok'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="card">
            <button
              disabled={!clientId || busy}
              onClick={async () => {
                setBusy(true);
                try {
                  const result = await apply({
                    csv, profile, fileName,
                    clientId: clientId as Id<'clients'>,
                    ...(dateMode ? { dateMode } : {}),
                  });
                  setDone(
                    `${result.assetsCreated} new items, ${result.assetsMatched} matched, ` +
                      `${result.testsImported} tests imported, ${result.jobsCreated} job(s) scheduled, ` +
                      `${result.skipped} row(s) sent to review.`,
                  );
                  setPreview(null);
                  setCsv('');
                } finally {
                  setBusy(false);
                }
              }}
            >
              {busy ? 'Importing…' : 'Import for real'}
            </button>
            {!clientId ? <p className="small muted">Choose a client first.</p> : null}
          </div>
        </>
      ) : null}

      {done ? <div className="card"><strong>Imported.</strong><p className="small">{done}</p></div> : null}

      {batches && batches.length > 0 ? (
        <>
          <h2>Past imports</h2>
          <div className="card">
            <table>
              <thead><tr><th>File</th><th>Source</th><th>Rows</th><th>Skipped</th><th>When</th></tr></thead>
              <tbody>
                {batches.map((b) => (
                  <tr key={b._id}>
                    <td>{b.fileName}</td>
                    <td className="muted">{b.profile}</td>
                    <td>{b.rowCount}</td>
                    <td className={b.unmatched ? 'overdue' : ''}>{b.unmatched}</td>
                    <td className="muted">{new Date(b.createdAt).toLocaleDateString('en-AU')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      ) : null}
    </>
  );
}
